import React, {useState, FC, useEffect, useCallback} from 'react'
import { 
  COLORS, 
  HGS_FIRMS, 
  IExpenseFilterModel,
  IKmFilterModel, 
  IServiceFilterModel,
  PASSIVE_REASON,
  subTabOptions,
  carTabs, 
  validationCar, 
  WEHICLE_STATUS,
} from './CarModel'
import {Form, Formik} from 'formik'
import {createCar, filterExpense, filterKM, filterService, updateCar} from './Api'
import {showErrorToast, showSuccessToast} from '../../../shared/components/ToastUtils'
import {ToastContainer} from 'react-toastify'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import {ACTIVE_OR_PASSIVE, BODY_TYPES, CLEANS, FUEL_LEVEL, FUEL_TYPES, IS_OR_NO, OPEN_OR_CLOSE, TRANSMISSION_TYPES} from '../../../shared/models/SharedConstantLookups'
import FormTabHeader from '../../../shared/components/FormTabHeader'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import SubTabHeader from '../../../shared/components/SubTabHeader'
import {
  branches,
  brands,
  locations,
  vehicleGroups,
  vehicleTypes,
} from '../../../shared/models/SharedApiLookups'
import {KmLogTable} from './tables/KmLogTable'
import {ServiceLogTable} from './tables/ServiceLogTable'
import {ExpenseLogTable} from './tables/ExpenseLogTable'
import { initialCarFormFieldValues, mapFormToCarModel } from './CarMapper'

const CarForm: FC = () => {
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'definition'>('definition')
  const [tabSub, setTabSub] = useState<string | 'fleet'>('fleet')
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{value: string; label: string}[]>(
    []
  )
  const [brandOptions, setBrandOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [locationOptions, setLocationOptions] = useState<{value: string; label: string}[]>([])
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<{value: string; label: string}[]>([]) 
  const [kmLogs, setKmLogs] = useState<IKmFilterModel[]>([])
  const [serviceLogs, setServiceLogs] = useState<IServiceFilterModel[]>([])
  const [expenseLogs, setExpenseLogs] = useState<IExpenseFilterModel[]>([])
  const [kmTotalPages, setKmTotalPages] = useState(0)
  const [serviceTotalPages, setServiceTotalPages] = useState(0)
  const [expenseTotalPages, setExpenseTotalPages] = useState(0)
  const [kmCurrentPage, setKmCurrentPage] = useState(0)
  const [serviceCurrentPage, setServiceCurrentPage] = useState(0)
  const [expenseCurrentPage, setExpenseCurrentPage] = useState(0)
  const [kmSortBy, setKmSortBy] = useState<string>('')
  const [serviceSortBy, setServiceSortBy] = useState<string>('')
  const [expenseSortBy, setExpenseSortBy] = useState<string>('')
  const [kmSortOrder, setKmSortOrder] = useState<'asc' | 'desc'>('asc')
  const [serviceSortOrder, setServiceSortOrder] = useState<'asc' | 'desc'>('asc')
  const [expenseSortOrder, setExpenseSortOrder] = useState<'asc' | 'desc'>('asc')
  const [loadingKm, setLoadingKm] = useState(false)
  const [loadingService, setLoadingService] = useState(false)
  const [loadingExpense, setLoadingExpense] = useState(false)
  const [kmSortPageSize, setKmSortPageSize] = useState(10)
  const [serviceSortPageSize, setServiceSortPageSize] = useState(10)
  const [expenseSortPageSize, setExpenseSortPageSize] = useState(10)

  useEffect(() => {
    Promise.all([vehicleGroups(), brands(), branches(), locations(), vehicleTypes()]).then(
      ([vg, br, brn, loc, vt]) => {
        setVehicleGroupOptions(vg)
        setBrandOptions(br)
        setBranchOptions(brn)
        setLocationOptions(loc)
        setVehicleTypeOptions(vt)
      }
    )
  }, [])

  const handleSave = async (values: any, resetForm: () => void) => {
    setLoading(true)
    try {
        const carData = mapFormToCarModel(values);
      if (selectedId) { 
        await updateCar(selectedId, carData);
        showSuccessToast('Araç başarıyla güncellendi')
      } else {
        await createCar(carData)
        showSuccessToast('Araç başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null) 
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleKmPageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < kmTotalPages) {
        setKmCurrentPage(newPage)
        filterKM(newPage, kmSortPageSize, kmSortBy, kmSortOrder)
      }
    },
    [kmTotalPages, kmSortBy, kmSortOrder, kmSortPageSize]
  )

  const handleServicePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < serviceTotalPages) {
        setServiceCurrentPage(newPage)
        filterService(newPage, serviceSortPageSize, serviceSortBy, serviceSortOrder)
      }
    },
    [serviceTotalPages, serviceSortBy, serviceSortOrder, serviceSortPageSize]
  )

  const handleExpensePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < expenseTotalPages) {
        setExpenseCurrentPage(newPage)
        filterExpense(newPage, expenseSortPageSize, expenseSortBy, expenseSortOrder)
      }
    },
    [expenseTotalPages, expenseSortBy, expenseSortOrder, expenseSortPageSize]
  )

  const handleKmSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setKmSortBy(column)
      setKmSortOrder(order)
      filterKM(kmCurrentPage, kmSortPageSize, column, order)
    },
    [kmCurrentPage, kmSortPageSize]
  )

  const handleServiceSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setServiceSortBy(column)
      setServiceSortOrder(order)
      filterService(serviceCurrentPage, serviceSortPageSize, column, order)
    },
    [serviceCurrentPage, serviceSortPageSize]
  )

  const handleExpenseSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setExpenseSortBy(column)
      setExpenseSortOrder(order)
      filterExpense(expenseCurrentPage, expenseSortPageSize, column, order)
    },
    [expenseCurrentPage, expenseSortPageSize]
  )

  const handleFilterKM = async () => {
    setLoadingKm(true) 
    try {
      const response = await filterKM(kmCurrentPage, kmSortPageSize, kmSortBy, kmSortOrder)
      if (response.data.hasError) {
        throw new Error(response.data.message || 'Km kayıtları getirilirken bir hata oluştu')
      }
      setKmLogs(response.data.payload.content)
      setKmTotalPages(response.data.payload.totalPages)
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Km kayıtları getirilirken bir hata oluştu')
    } finally {
      setLoadingKm(false)
    }
  }

  const handleFilterExpense = async () => {
    setLoadingExpense(true)
      try {
      const response = await filterExpense(
        expenseCurrentPage,
        expenseSortPageSize,
        expenseSortBy,
        expenseSortOrder
      )
      if (response.data.hasError) {
        throw new Error(response.data.message || 'Gider kayıtları getirilirken bir hata oluştu')
      }
      setExpenseLogs(response.data.payload.content)
      setExpenseTotalPages(response.data.payload.totalPages)
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Gider kayıtları getirilirken bir hata oluştu')
    } finally {
      setLoadingExpense(false)
    }
  }

  const handleFilterService = async () => {
    setLoadingService(true) 
    try {
      const response = await filterService(
        serviceCurrentPage,
        serviceSortPageSize,
        serviceSortBy,
        serviceSortOrder
      )
      if (response.data.hasError) {
        throw new Error(response.data.message || 'Servis kayıtları getirilirken bir hata oluştu')
      }
      setServiceLogs(response.data.payload.content)
      setServiceTotalPages(response.data.payload.totalPages)
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Servis kayıtları getirilirken bir hata oluştu')
    } finally {
      setLoadingService(false)
    }
  }

  const handleKmPageSizeChange = (newPageSize: number) => {
    setKmSortPageSize(newPageSize)
    filterKM(kmCurrentPage, newPageSize, kmSortBy, kmSortOrder)
  }

  const handleServicePageSizeChange = (newPageSize: number) => {
    setServiceSortPageSize(newPageSize)
    filterService(serviceCurrentPage, newPageSize, serviceSortBy, serviceSortOrder)
  }

  const handleExpensePageSizeChange = (newPageSize: number) => {
    setExpenseSortPageSize(newPageSize)
    filterExpense(expenseCurrentPage, newPageSize, expenseSortBy, expenseSortOrder)
  }


  return (
    <>
      <Formik
        initialValues={initialCarFormFieldValues}
        validationSchema={validationCar}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }
          return (
            <>
              <FormTabHeader
                formId='carForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={carTabs}
                onExportExcel={() => console.log('Excel export')}
                smallButton={false}
              />
              <div className='card mb-5'>
                <Form id='carForm' className='form card-body'>
                  {tab === 'definition' && (
                    <>
                      <div className='row mb-6'>
                        <TextInput name='plate' label='Plaka Gir' wrapperClassName='col-md-6' />
                        <SelectInput
                          name='branch'
                          label='Şube'
                          wrapperClassName='col-md-6'
                          options={branchOptions}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='brand'
                          label='Marka'
                          wrapperClassName='col-md-6'
                          options={brandOptions}
                        />
                        <SelectInput
                          name='location'
                          label='Lokasyon'
                          wrapperClassName='col-md-6'
                          options={locationOptions}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='type'
                          label='Tip'
                          wrapperClassName='col-md-6'
                          options={vehicleTypeOptions}
                        />
                        <SelectInput
                          name='carStatus'
                          label='Durum'
                          wrapperClassName='col-md-6'
                          options={ACTIVE_OR_PASSIVE}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='vehicleGroup'
                          label='Araç Grubu'
                          wrapperClassName='col-md-6'
                          options={vehicleGroupOptions}
                        />
                        <SelectInput
                          name='passiveReason'
                          label='Pasif Etme Sebebi'
                          wrapperClassName='col-md-6'
                          options={PASSIVE_REASON}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='transmission'
                          label='Vites'
                          wrapperClassName='col-md-6'
                          options={TRANSMISSION_TYPES}
                        />
                        <SelectInput
                          name='color'
                          label='Renk'
                          wrapperClassName='col-md-6'
                          options={COLORS}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='fuelType'
                          label='Yakıt Türü'
                          wrapperClassName='col-md-6'
                          options={FUEL_TYPES}
                        />
                        <TextInput
                          name='lastKm'
                          type='number'
                          label='Son Km'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='model'
                          type='number'
                          label='Model (Yılı)'
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='lastFuel'
                          label='Son Yakıt'
                          wrapperClassName='col-md-6'
                          options={FUEL_LEVEL}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='webReservation'
                          label='Web Rezervasyon'
                          wrapperClassName='col-md-6'
                          options={OPEN_OR_CLOSE}
                        />
                        <SelectInput
                          name='officeReservation'
                          label='Ofis Rezervasyonu'
                          wrapperClassName='col-md-6'
                          options={OPEN_OR_CLOSE}
                        />
                      </div>
                    </>
                  )}
                  {tab === 'detail' && (
                    <>
                      <div className='row mb-6'>
                        <SelectInput
                          name='vehicleStatus'
                          label='Araç Durumu'
                          wrapperClassName='col-md-6'
                          options={WEHICLE_STATUS}
                        />
                        <SelectInput
                          name='ogsHgsAvailable'
                          label='OGS/HGS Var mı'
                          wrapperClassName='col-md-6'
                          options={IS_OR_NO}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='bodyType'
                          label='Kasa Tipi'
                          wrapperClassName='col-md-6'
                          options={BODY_TYPES}
                        />
                        <SelectInput
                          name='hgsCompany'
                          label='HGS Firması'
                          wrapperClassName='col-md-6'
                          options={HGS_FIRMS}
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='licenseDate'
                          type='date'
                          label='Ruhsat Tarihi'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='ogsHgsNumber'
                          label='HGS/OGS Numarası'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='registrationDate'
                          type='date'
                          label='Tescil Tarihi'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='ogsHgsLoad'
                          type='number'
                          label='HGS Yükle'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='licenseDocumentNo'
                          label='Ruhsat Belge No'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='integrationCode'
                          label='Entegrasyon Kodu'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput name='chassisNumber' label='Şasi No' wrapperClassName='col-md-6' />
                        <TextInput name='radioCode' label='Teyp Kodu' wrapperClassName='col-md-6' />
                      </div>
                      <div className='row mb-6'>
                        <TextInput name='engineNumber' label='Motor No' wrapperClassName='col-md-6' />
                        <TextInput
                          name='vehicleTrackingNumber'
                          label='Araç Takip No'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='enginePower'
                          type='number'
                          label='Motor Gücü'
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='insuranceFollow'
                          label='Sigorta Takibi'
                          wrapperClassName='col-md-6'
                          options={IS_OR_NO}
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='cylinderVolume'
                          type='number'
                          label='Silindir Hacmi'
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='periodicServiceTracking'
                          label='Periyodik Servis Takibi'
                          wrapperClassName='col-md-6'
                          options={IS_OR_NO}
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextAreaInput name='detail' label='Detay' wrapperClassName='col-md-6' />
                        <SelectInput
                          name='cleaningStatus'
                          label='Temizlik Durumu'
                          wrapperClassName='col-md-6'
                          options={CLEANS}
                        />
                      </div>
                    </>
                  )}
                  {tab === 'insurance' && (
                    <>
                      <div className='row mb-6'>
                        <TextInput
                          name='trafficInsuranceStartDate'
                          type='date'
                          label='Trafik Sig. Baş. Tar.'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='cascoStartDate'
                          type='date'
                          label='Kasko Baş. Tar.'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='trafficInsuranceEndDate'
                          type='date'
                          label='Trafik Sig. Bit. Tar.'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='cascoEndDate'
                          type='date'
                          label='Kasko Bitiş Tarihi'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='insuranceCompany'
                          label='Sigorta Firması'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='cascoCompany'
                          label='Kasko Firma'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='insuranceAgency'
                          label='Sigorta Acente'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='cascoAgency'
                          label='Kasko Acente'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='trafficPolicyNumber'
                          label='Trafik Poliçe No'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='cascoPolicyNumber'
                          label='Kasko Poliçe No'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='cascoCode'
                          label='Kasko Kodu'
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='inspectionStartDate'
                          type='date'
                          label='Muayene Baş. Tar.'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='inspectionEndDate'
                          type='date'
                          label='Muayene Bit. Tar.'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                    </>
                  )}
                  {tab === 'warning' && (
                    <>
                      <SubTabHeader tab={tabSub} setTab={setTabSub} tabs={subTabOptions} />
                      {tabSub === 'fleet' && (
                        <>
                          <div className='row mb-6'>
                            <TextInput
                              name='fleetEntryDate'
                              type='date'
                              label='Filo Giriş Tarihi'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='cascoAmount'
                              currency={true}
                              label='Kasko Bedeli'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='purchasingCompany'
                              label='Alım Firması'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='closeDate'
                              type='date'
                              label='Kapatma Tarihi'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <SelectInput
                              name='pledgeStatus'
                              label='Rehin Durumu'
                              wrapperClassName='col-md-6'
                              options={IS_OR_NO}
                            />
                            <SelectInput
                              name='vehicleOwner'
                              label='Araç Sahibi'
                              wrapperClassName='col-md-6'
                              options={IS_OR_NO}
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='vehicleOwnerCompany'
                              label='Araç Sahibi Firma'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='vehicleOwnerNumber'
                              label='Araç Sahibi No'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='secondaryVehicleOwner'
                              label='Araç Sahibi 2 No'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='purchaseTotalPrice'
                              currency={true}
                              label='Alım Bedeli Toplam'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='purchasePriceExclTax'
                              currency={true}
                              label='Alım Bedeli Vergisiz'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='purchaseVAT'
                              currency={true}
                              label='Alım Bedeli KDV'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='purchaseSCT'
                              currency={true}
                              label='Alım Bedeli ÖTV'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='purchaseDate'
                              type='date'
                              label='Alım Tarihi'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='purchaseInvoiceNumber'
                              type='number'
                              label='Alım Fatura No'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='vehicleSecondHandPrice'
                              currency={true}
                              label='Araç 2.El Fiyatı'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='fleetManagementCost'
                              currency={true}
                              label='Filo Yönetim Maliyeti'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='monthlyCost'
                              currency={true}
                              label='Aylık Maliyet'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='additionalPrice'
                              currency={true}
                              label='Ek (İlave) Fiyat'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='creditCompany'
                              label='Kredi Firması'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='externalSupplierKmLimit'
                              type='number'
                              label='Dış Tedarik Km Limiti'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='assistServiceProvider'
                              label='Hizmet Veren Assist Firma'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            {[1, 2, 3, 4, 5].map((i) => (
                              <div className='row mb-6'>
                                <TextInput
                                  name={`specialKod${i}`}
                                  label={`Özel Kod ${i}`}
                                  wrapperClassName='col-md-6'
                                />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {tabSub === 'desc' && (
                        <>
                          <div className='row mb-6'>
                            <TextInput
                              name='fleetExitDate'
                              type='date'
                              label='Filo Çıkış Tarihi'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='plannedExitDate'
                              type='date'
                              label='Çıkması Planlanan Tarih'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='saleWarningKm'
                              type='number'
                              label='Satış Uyarı KM'
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='snowTireStatus'
                              label='Kar Lastiği'
                              wrapperClassName='col-md-6'
                              options={IS_OR_NO}
                            />
                          </div>

                          <div className='row mb-6'>
                            <SelectInput
                              name='spareKeyStatus'
                              label='Yedek Anahtar'
                              wrapperClassName='col-md-6'
                              options={IS_OR_NO}
                            />
                            <TextInput
                              name='tireWarningKm'
                              type='number'
                              label='Lastik Uyarı KM'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextAreaInput
                              name='description'
                              label='Açıklama'
                              wrapperClassName='col-md-6'
                            />
                            <TextAreaInput
                              name='tireInfo'
                              label='Lastik Bilgisi'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'filo_detail' && (
                        <>
                          <div className='row mb-6'>
                            <TextInput
                              name='lastMaintenanceNumber'
                              label='Son Bakım No'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastMaintenanceKm'
                              type='number'
                              label='Son Bakım Km'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastMaintenanceDate'
                              type='date'
                              label='Son Bakım Tar.'
                              wrapperClassName='col-md-4'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='kmInspectionNumber'
                              label='Km Tespit No'
                              wrapperClassName='col-md-4'
                            />
                            <SelectInput
                              name='inspectionType'
                              label='Son Tespit Türü'
                              wrapperClassName='col-md-4'
                              options={[
                                {value: 'periodic', label: 'Periyodik Bakım'},
                                {value: 'maintenance', label: 'Genel Bakım'},
                                {value: 'repair', label: 'Arıza Onarım'},
                                {value: 'tire_change', label: 'Lastik Değişimi'},
                              ]}
                            />
                            <TextInput
                              name='kmInspectionDate'
                              type='date'
                              label='Km Tespit Tar'
                              wrapperClassName='col-md-4'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='lastPeriodicNumber'
                              label='Son Per. No'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastPeriodicKm'
                              type='number'
                              label='Son Per. Km'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastPeriodicDate'
                              type='date'
                              label='Son Per. Tar.'
                              wrapperClassName='col-md-4'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='lastTireNumber'
                              label='Son Lastik No'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastTireKm'
                              type='number'
                              label='Son Lastik Km'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='lastTireDate'
                              type='date'
                              label='Son Lastik Tar'
                              wrapperClassName='col-md-4'
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'km' && (
                        <>
                          <div className='card'>
                            <div className='card-header border-0 pt-6'>
                              <div className='card-title'>
                                <button
                                  type='button'
                                  className='btn btn-primary'
                                  onClick={handleFilterKM}
                                >
                                  <i className='bi bi-filter fs-2'></i>
                                  Kayıtları Göster
                                </button>
                              </div>
                            </div>
                            <div className='card-body'>
                              <KmLogTable
                                data={kmLogs}
                                currentPage={kmCurrentPage}
                                totalPages={kmTotalPages}
                                pageSize={kmSortPageSize}
                                onPageSizeChange={(pageSize) => handleKmPageSizeChange(pageSize)}
                                onPageChange={handleKmPageChange} 
                                sortBy={kmSortBy}
                                sortOrder={kmSortOrder}
                                onSort={handleKmSort}
                                loading={loadingKm}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {tabSub === 'service' && (
                        <>
                          <div className='card'>
                            <div className='card-header border-0 pt-6'>
                              <div className='card-title'>
                                <button
                                  type='button'
                                  className='btn btn-primary'
                                  onClick={handleFilterService}
                                >
                                  <i className='bi bi-filter fs-2'></i>
                                  Kayıtları Göster
                                </button>
                              </div>
                            </div>
                            <div className='card-body'>
                              <ServiceLogTable
                                data={serviceLogs}
                                currentPage={serviceCurrentPage}
                                totalPages={serviceTotalPages}
                                pageSize={serviceSortPageSize}
                                onPageSizeChange={(pageSize) => handleServicePageSizeChange(pageSize)}
                                onPageChange={handleServicePageChange}
                                sortBy={serviceSortBy}
                                sortOrder={serviceSortOrder}
                                onSort={handleServiceSort}
                                loading={loadingService}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {tabSub === 'expence' && (
                        <>
                          <div className='card'>
                            <div className='card-header border-0 pt-6'>
                              <div className='card-title'>
                                <button
                                  type='button'
                                  className='btn btn-primary'
                                  onClick={handleFilterExpense}
                                >
                                  <i className='bi bi-filter fs-2'></i>
                                  Kayıtları Göster
                                </button>
                              </div>
                            </div>
                            <div className='card-body'>
                              <ExpenseLogTable
                                data={expenseLogs}
                                currentPage={expenseCurrentPage}
                                totalPages={expenseTotalPages}
                                pageSize={expenseSortPageSize}
                                onPageSizeChange={(pageSize) => handleExpensePageSizeChange(pageSize)}
                                onPageChange={handleExpensePageChange}
                                sortBy={expenseSortBy}
                                sortOrder={expenseSortOrder}
                                onSort={handleExpenseSort}
                                loading={loadingExpense}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {tab === 'storage' && <></>}
                </Form>
              </div>
            </>
          )
        }}
      </Formik>
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

const CarPage: FC = () => {
  return <CarForm />
}

export {CarPage}
